import React, { useRef, useState } from "react";
import { Popover, IconButton } from "@material-ui/core";
import styled from "styled-components";
import Confetti from "react-confetti-boom";

import { Icon } from "js/Components/Icon";
import { HoverHighlight } from "./HoverHighlight";

const CloseButtonContainer = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1000;
`;

const CloseButton = styled(IconButton)`
    &&& {
        padding: 8px;
    }
`;

const CompletionTitle = styled.div`
    font-family: "Source Sans Pro";
    font-size: 18px;
    font-weight: 600;
    line-height: 120%;
    letter-spacing: 0.5px;
`;

const CompletionBody = styled.div`
    font-family: "Source Sans Pro";
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 125%;
    letter-spacing: -0.32px;
`;

const InstructionText = styled.div`
    font-family: "Source Sans Pro";
    font-size: 16px;
    font-style: italic;
    font-weight: 400;
    line-height: 125%;
    letter-spacing: -0.32px;
`;

const QuestionMarkIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: #E5E5E5;
    font-size: 14px;
    font-weight: 600;
    font-style: normal;
    color: #666666;
    margin: 0 4px;
    vertical-align: middle;
    line-height: 1;
    text-align: center;
    letter-spacing: 0;
`;

const ContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const ConfettiWrapper = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    pointer-events: none;
    z-index: 10000;
`;

interface CompletedStepProps {
    open: boolean;
    onClose: () => void;
}

export const CompletedStep: React.FC<CompletedStepProps> = ({ open, onClose }) => {
    const instructionTextRef = useRef<HTMLDivElement>(null);
    const [confettiPosition, setConfettiPosition] = useState<{ x: number; y: number } | null>(null);

    const handlePopoverEntered = (element: HTMLElement | null) => {
        if (element) {
            const popoverRect = element.getBoundingClientRect();
            const centerX = (popoverRect.left + popoverRect.width / 2) / window.innerWidth;
            const topY = popoverRect.top / window.innerHeight;
            setConfettiPosition({ x: centerX, y: topY });
        }
    };

    if (!open) {
        return null;
    }

    return (
        <>
            {open && confettiPosition && (
                <ConfettiWrapper>
                    {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                    {/* @ts-expect-error - react-confetti-boom has incorrect type definitions */}
                    <Confetti
                        mode="boom"
                        x={confettiPosition.x}
                        y={confettiPosition.y}
                        particleCount={200}
                        colors={["#3F5FA3", "#46C0E7", "#8D31E6", "#D63A9A", "#E4572E", "#F08F4E"]}
                        deg={270}
                        spreadDeg={15}
                        shapeSize={8}
                        launchSpeed={1}
                        effectInterval={2000}
                        effectCount={1}
                    />
                </ConfettiWrapper>
            )}
            <Popover
                open={open}
                onClose={onClose}
                disableEscapeKeyDown
                anchorReference="anchorPosition"
                anchorPosition={{ top: window.innerHeight - 24, left: window.innerWidth - 82 }}
                transformOrigin={{
                    vertical: "bottom",
                    horizontal: "right"
                }}
                TransitionProps={{
                    onEntered: (element: HTMLElement) => handlePopoverEntered(element)
                }}
                PaperProps={{
                    style: {
                        width: 320,
                        background: "#FFFFFF",
                        borderRadius: 8,
                        boxShadow: "0 9px 37px 0 rgba(0, 0, 0, 0.20)",
                        border: "1px solid #222",
                        padding: 24,
                        position: "relative",
                        zIndex: 10
                    }
                }}
                disableRestoreFocus
            >
                <CloseButtonContainer>
                    <CloseButton
                        onClick={onClose}
                        size="small"
                    >
                        <Icon color="black" medium style={{ background: "white", borderRadius: "50px" }}>close</Icon>                    </CloseButton>
                </CloseButtonContainer>
                <ContentContainer>
                    <CompletionTitle>
                        What else will you do?
                    </CompletionTitle>
                    <CompletionBody>
                        You've nailed the basics. Now it's time to play—add even more Smart Slides, experiment with layouts, or create your own theme. You've got this.
                    </CompletionBody>
                    <InstructionText ref={instructionTextRef}>
                        To revisit this helpful checklist, find it under the <QuestionMarkIcon>?</QuestionMarkIcon> icon.
                    </InstructionText>
                </ContentContainer>
            </Popover>
            <HoverHighlight
                target="#help-icon"
                hoverTriggerRef={instructionTextRef}
            />
        </>
    );
};

